/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * License Matching Utilities
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 *
 * This module provides intelligent license-to-product matching,
 * handling both new licenses (with StripeProductId) and legacy
 * licenses (without StripeProductId) gracefully.
 */

import {
  getStripeToQlm,
  getTslFeaturesToStripe,
  getTslPaidProducts,
} from "@/services/productIdConfig";
import { logger } from "@/lib/logger";
import { License } from "@/types";

/**
 * Result of a license matching operation with diagnostic information.
 */
export interface LicenseMatchResult {
  licenses: License[];
  matchType: "exact" | "legacy" | "none";
  legacyMatchCount: number;
  message: string;
}

/**
 * Filter an array of licenses to only those matching a specific product.
 *
 * Uses intelligent matching:
 * 1. First, tries exact StripeProductId matching
 * 2. For legacy licenses (no StripeProductId), matches by QLM ProductID
 * 3. Unmatched fallback: only for single-product orders to prevent contamination
 *
 * @param licenses - Array of licenses to filter
 * @param stripeProductId - The Stripe product ID to match
 * @param totalProductsInOrder - Number of distinct products in the order.
 *        When > 1, the unmatched fallback is suppressed to prevent cross-product
 *        contamination. Defaults to 1 (single-product, fallback allowed).
 * @returns Filtered array of matching licenses with diagnostic info
 */
export function filterLicensesByProduct(
  licenses: License[],
  stripeProductId: string,
  totalProductsInOrder: number = 1,
): LicenseMatchResult {
  if (!licenses || licenses.length === 0) {
    return {
      licenses: [],
      matchType: "none",
      legacyMatchCount: 0,
      message: "No licenses provided for matching",
    };
  }

  const exactMatches: License[] = [];
  const legacyMatches: License[] = [];
  const unmatchedLicenses: License[] = [];

  const expectedQlmProductId = getStripeToQlm()[stripeProductId];

  for (const license of licenses) {
    // Issue 1 (FCR Pass 11): TSL paid plans share QLM ProductID 22. Features is
    // the ONLY reliable tier signal ("0:2;" = 45K, "0:4;" = 100K). Do NOT match
    // on ProductID 22 alone — both TSL cards have expectedQlmProductId == 22, so
    // a ProductID-first check would match a 45K license onto the 100K card (and
    // vice versa), causing the cross-card duplication Sakshi reported.
    // The old block here used stale ids 25/27 (no longer exist); it is removed.
    // The Features-first paths below (SK-1 override + Fix 6 fallback) are correct
    // and are the only paths that should run for TSL paid products.

    // Primary: Exact StripeProductId match — with stale-StripeProductId guard for TSL upgrades.
    // After a 45K→100K plan upgrade the QLM record still carries the old prod_SWL3...
    // in StripeProductId (set at original purchase) but its Features field has been
    // updated to "0:4;" (100K tier). When Features maps to a DIFFERENT TSL product
    // than StripeProductId, skip the exact match so the license falls through to the
    // Features-based override path below. (SK-1 fix, S306)
    if (license.StripeProductId === stripeProductId) {
      if (license.Features && getTslPaidProducts().has(stripeProductId)) {
        const featProductId = getTslFeaturesToStripe()[license.Features.trim()];
        if (featProductId && featProductId !== stripeProductId) {
          // StripeProductId is stale from the original purchase; let the Features
          // override below match it correctly to the post-upgrade plan tier.
        } else {
          exactMatches.push(license);
          continue;
        }
      } else {
        exactMatches.push(license);
        continue;
      }
    }

    // Plan-upgrade Features override (SK-1). When a TSL license's StripeProductId
    // still points to the old plan tier (stale from original purchase) but its
    // Features field has been updated to reflect the new tier, route it to the
    // correct product card using Features as the authoritative plan indicator.
    if (
      license.StripeProductId &&
      license.StripeProductId !== stripeProductId &&
      getTslPaidProducts().has(stripeProductId) &&
      license.Features
    ) {
      const featProductId = getTslFeaturesToStripe()[license.Features.trim()];
      if (featProductId === stripeProductId) {
        exactMatches.push(license);
        continue;
      }
    }

    // Fix 6 (FCR 246): TSL Features-field fallback — secondary defensive layer.
    // When StripeProductId could not be resolved from UserData1 (e.g. UserData1 is a
    // dynamic cumulative total like "55000" after an add-on purchase), check the stable
    // Features field. Features is set once at license creation and never changes.
    if (!license.StripeProductId && license.Features) {
      const featProductId = getTslFeaturesToStripe()[license.Features.trim()];
      if (featProductId === stripeProductId) {
        exactMatches.push(license);
        continue;
      }
      // Features maps to a known but DIFFERENT TSL product — this license belongs
      // to a different plan. Exclude it entirely to prevent cross-product
      // contamination (e.g., a 45K license must not appear in a 100K query).
      if (featProductId !== undefined) {
        continue;
      }
    }

    // Legacy: For licenses without StripeProductId, match by QLM ProductID
    // NF-2 guard: expectedQlmProductId === 0 means a placeholder value for a
    // unified product whose QLM ID has not yet been configured. Treating 0 as a
    // valid match criterion would incorrectly match any malformed license record
    // that somehow carries ProductID=0. Skip the legacy path for 0-valued entries.
    if (!license.StripeProductId && expectedQlmProductId !== undefined && expectedQlmProductId > 0) {
      if (license.ProductID === expectedQlmProductId) {
        legacyMatches.push(license);
        continue;
      }
      // ProductID doesn't match QLM mapping — retain rather than silently
      // dropping, since the caller may have scoped the fetch by payment intent.
      unmatchedLicenses.push(license);
      continue;
    }

    // Fallback: License has no StripeProductId AND no QLM mapping exists
    // for this Stripe product. Retain it rather than silently dropping it,
    // since the caller already scoped the fetch to the correct payment intent.
    // NF-2: also treat expectedQlmProductId === 0 as "no mapping" so that
    // placeholder-valued unified products behave identically to unmapped ones.
    if (!license.StripeProductId && (expectedQlmProductId === undefined || expectedQlmProductId === 0)) {
      unmatchedLicenses.push(license);
    }
  }

  // Log legacy matches for tracking/migration purposes
  if (legacyMatches.length > 0) {
    logger.info(
      `[LicenseMatching] Legacy license match for product ${stripeProductId}: ` +
        `${legacyMatches.length} license(s) matched by ProductID=${expectedQlmProductId}`,
    );
  }

  // Log unmatched fallback licenses for migration tracking
  if (unmatchedLicenses.length > 0) {
    logger.info(
      `[LicenseMatching] Unmatched fallback for product ${stripeProductId}: ` +
        `${unmatchedLicenses.length} license(s) without StripeProductId or QLM mapping`,
    );
  }

  // Prioritized return: exact matches take precedence, then legacy, then
  // unmatched as last resort. This prevents cross-product contamination in
  // multi-product orders where unmatched licenses would otherwise appear
  // under every product.
  if (exactMatches.length > 0) {
    return {
      licenses: exactMatches,
      matchType: "exact",
      legacyMatchCount: 0,
      message: `Found ${exactMatches.length} exact match(es)`,
    };
  }

  if (legacyMatches.length > 0) {
    return {
      licenses: legacyMatches,
      matchType: "legacy",
      legacyMatchCount: legacyMatches.length,
      message: `Found ${legacyMatches.length} legacy match(es)`,
    };
  }

  // Only use unmatched as last resort for SINGLE-PRODUCT orders.
  // For multi-product orders, returning unmatched licenses causes the same
  // set of licenses to appear under every product (cross-product contamination).
  if (unmatchedLicenses.length > 0 && totalProductsInOrder <= 1) {
    return {
      licenses: unmatchedLicenses,
      matchType: "none",
      legacyMatchCount: 0,
      message: `Using ${unmatchedLicenses.length} unmatched fallback(s) (single-product order)`,
    };
  }

  if (unmatchedLicenses.length > 0 && totalProductsInOrder > 1) {
    logger.warn(
      `[LicenseMatching] Suppressed ${unmatchedLicenses.length} unmatched license(s) ` +
        `for product ${stripeProductId} in multi-product order ` +
        `(${totalProductsInOrder} products). Returning 0 to prevent contamination.`,
    );
  }

  return {
    licenses: [],
    matchType: "none",
    legacyMatchCount: 0,
    message: `No licenses found for product ${stripeProductId}`,
  };
}

/**
 * Check if a license is a legacy license (missing StripeProductId).
 *
 * @param license - The license to check
 * @returns true if the license has no StripeProductId
 */
export function isLegacyLicense(license: License): boolean {
  return !license.StripeProductId;
}

/**
 * S-4 (FCR S509 Pass 10): Check if a license is expired or revoked.
 *
 * Checks both the boolean ``IsExpired`` flag (set by the backend's
 * ``_add_activation_info`` when ``Disabled=1`` or ``SubscriptionExpiryDate``
 * has passed) and the text-based ``ActivationStatus`` field, since either
 * one can be the authoritative signal depending on which code path
 * populated the license record.
 *
 * @param license - The license to check
 * @returns true if the license is expired or revoked
 */
export function isLicenseExpiredOrRevoked(license: License): boolean {
  return (
    Boolean(license.IsExpired) ||
    license.ActivationStatus?.toLowerCase().includes("expired") === true
  );
}

/**
 * S-4 (FCR S509 Pass 10): Prefer active (non-expired, non-revoked) licenses
 * when more than one license is present.
 *
 * Root cause this closes: on a multi-product order where two products
 * share a single payment intent, refunding one product revokes that
 * product's QLM license but leaves the sibling's license active. A
 * payment-intent-scoped license lookup can then return BOTH keys for the
 * kept product -- one revoked (the refunded sibling's leftover record) and
 * one active. Without this filter, display/routing logic that simply takes
 * the first license in the array can surface the expired key, showing the
 * customer a "License Already Activated" or expired state for a product
 * they still own and are still paying for.
 *
 * If every license in the array is expired, all are returned unfiltered --
 * an empty result would be worse than showing the truth (no active license
 * exists for this product).
 *
 * @param licenses - Array of licenses to filter
 * @returns Active licenses if any exist, otherwise the original array
 */
export function preferActiveLicenses(licenses: License[]): License[] {
  if (!licenses?.length) return licenses ?? [];
  const active = licenses.filter((l) => !isLicenseExpiredOrRevoked(l));
  return active.length > 0 ? active : licenses;
}

